import {
  AppstoreOutlined,
  AuditOutlined,
  ClusterOutlined,
  DashboardOutlined,
  LogoutOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  SafetyCertificateOutlined,
  ScheduleOutlined,
  SettingOutlined,
  TableOutlined,
  TeamOutlined,
  UserOutlined,
} from '@ant-design/icons'
import { Layout as AntLayout, Button, Dropdown, Menu, theme } from 'antd'
import { useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useAuthStore } from '@/stores/auth'
import { useConstantsStore } from '@/stores/constants'

const { Sider, Content, Header } = AntLayout

const menuItems = [
  { key: '/', icon: <DashboardOutlined />, label: 'Dashboard' },
  { key: '/tenants', icon: <TeamOutlined />, label: 'Tenants' },
  { key: '/clusters', icon: <ClusterOutlined />, label: 'Clusters' },
  { key: '/reports', icon: <TableOutlined />, label: 'Reports' },
  { key: '/templates', icon: <AppstoreOutlined />, label: 'Templates' },
  { key: '/tasks', icon: <ScheduleOutlined />, label: 'Tasks' },
  { key: '/adx-compliance', icon: <SafetyCertificateOutlined />, label: 'ADX Compliance' },
  { key: '/audit-logs', icon: <AuditOutlined />, label: 'Audit Logs' },
  { key: '/settings', icon: <SettingOutlined />, label: 'Settings' },
]

interface LayoutProps {
  children: React.ReactNode
}

export function Layout({ children }: LayoutProps) {
  const [collapsed, setCollapsed] = useState(false)
  const navigate = useNavigate()
  const location = useLocation()
  const { token } = theme.useToken()
  const fetchConstants = useConstantsStore((state) => state.fetchConstants)
  const { user, clearAuth } = useAuthStore()

  useEffect(() => {
    fetchConstants()
  }, [fetchConstants])

  const getSelectedKey = () => {
    const path = location.pathname
    const match = menuItems.find((item) => item.key !== '/' && path.startsWith(item.key))
    return match ? match.key : '/'
  }

  const getPageTitle = () => {
    const path = location.pathname
    const match = menuItems.find((item) => item.key !== '/' && path.startsWith(item.key))
    return match?.label || 'Dashboard'
  }

  const siderWidth = collapsed ? 80 : 200

  const handleLogout = () => {
    clearAuth()
    navigate('/login', { replace: true })
  }

  const userMenuItems = [
    {
      key: 'profile',
      icon: <UserOutlined />,
      label: 'Profile',
      onClick: () => navigate('/profile'),
    },
    {
      type: 'divider' as const,
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: 'Logout',
      onClick: handleLogout,
    },
  ]

  return (
    <AntLayout className="min-h-screen">
      <Sider
        collapsible
        collapsed={collapsed}
        onCollapse={setCollapsed}
        trigger={null}
        width={200}
        collapsedWidth={80}
        style={{
          position: 'fixed',
          left: 0,
          top: 0,
          bottom: 0,
          height: '100vh',
          overflow: 'auto',
          zIndex: 100,
        }}
      >
        <button
          type="button"
          style={{
            height: 64,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            background: 'none',
            border: 'none',
            width: '100%',
            padding: 0,
          }}
          onClick={() => navigate('/')}
        >
          {collapsed ? (
            <span
              style={{
                fontSize: 24,
                fontWeight: 700,
                color: '#fff',
              }}
            >
              O
            </span>
          ) : (
            <span
              style={{
                fontSize: 20,
                fontWeight: 600,
                color: '#fff',
              }}
            >
              OraX Platform
            </span>
          )}
        </button>
        <Menu
          theme="dark"
          mode="inline"
          selectedKeys={[getSelectedKey()]}
          items={menuItems}
          onClick={({ key }) => navigate(key)}
          style={{ borderRight: 0 }}
        />
      </Sider>

      <AntLayout style={{ marginLeft: siderWidth, transition: 'margin-left 0.2s' }}>
        <Header
          style={{
            padding: '0 24px',
            background: token.colorBgContainer,
            borderBottom: `1px solid ${token.colorBorderSecondary}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <Button
              type="text"
              icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
              onClick={() => setCollapsed(!collapsed)}
              style={{ fontSize: 16, width: 40, height: 40 }}
            />
            <span style={{ fontSize: 16, fontWeight: 500 }}>{getPageTitle()}</span>
          </div>
          <Dropdown menu={{ items: userMenuItems }} placement="bottomRight">
            <Button type="text" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <UserOutlined />
              <span style={{ color: token.colorTextSecondary }}>{user?.email || 'Admin'}</span>
            </Button>
          </Dropdown>
        </Header>

        <Content style={{ margin: 24 }}>{children}</Content>
      </AntLayout>
    </AntLayout>
  )
}
